import { Mail, Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SelectMenu as Select,
  useToast,
} from '@/components/ui';
import {
  useDeleteEmailTemplate,
  useEmailTemplates,
  useUpdateEmailTemplate,
} from '@/hooks/useEmailTemplates';
import usePersistedState from '@/hooks/usePersistedState';
import { useAppSelector } from '@/store';
import { EmailTemplate, EmailTemplateCategory } from '@/types';
import { EMAIL_TEMPLATE_CATEGORY_OPTIONS, ROUTES } from '@/utils/constants';
import { formatDate } from '@/utils/format';

const categoryVariant: Record<
  EmailTemplateCategory,
  'info' | 'success' | 'warning' | 'danger' | 'neutral'
> = {
  sales: 'info',
  billing: 'success',
  renewal: 'warning',
  service: 'neutral',
  internal: 'danger',
};

const TH =
  'whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-5 py-3.5 text-sm';

const AdminEmailTemplatesPage = () => {
  const { toast } = useToast();
  const isAdmin = useAppSelector((state) => state.auth.user?.role === 'admin');
  // Category filter persists across refresh and navigating to the editor and
  // back (sessionStorage, see usePersistedState).
  const [category, setCategory] = usePersistedState<EmailTemplateCategory | ''>(
    'adminEmailTemplates:category',
    '',
  );
  const {
    data: templates = [],
    isLoading,
    isError,
    isFetching,
  } = useEmailTemplates(category ? { category } : undefined);
  const deleteTemplate = useDeleteEmailTemplate();
  const updateTemplate = useUpdateEmailTemplate();
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);

  // Unused templates can be permanently deleted; a template that has already
  // sent an email can only be toggled active/inactive (ADR-021).
  const onConfirmDelete = (): void => {
    if (!deleteTarget) return;
    deleteTemplate.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`Deleted "${deleteTarget.name}"`);
        setDeleteTarget(null);
      },
      onError: () => toast.error('Could not delete that template. It may have already been used.'),
    });
  };

  const toggleActive = (template: EmailTemplate): void => {
    updateTemplate.mutate(
      { id: template.id, payload: { isActive: !template.isActive } },
      {
        onSuccess: () =>
          toast.success(
            template.isActive ? `Deactivated "${template.name}"` : `Activated "${template.name}"`,
          ),
        onError: () => toast.error('Could not update that template. Please try again.'),
      },
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: 5 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={i}>
          <td colSpan={7} className="px-5 py-3.5">
            <div className="h-4 w-full max-w-md animate-pulse rounded bg-line/50" aria-hidden />
            <span className="sr-only">Loading</span>
          </td>
        </tr>
      ));
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={7} className="px-5 py-12 text-center text-sm text-danger">
            Failed to load templates. Please try again.
          </td>
        </tr>
      );
    }

    if (templates.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="px-5 py-12 text-center text-sm text-ink/50">
            No templates yet. Create one to get started.
          </td>
        </tr>
      );
    }

    return templates.map((template) => (
      <tr key={template.id} className="transition-colors hover:bg-cream/50">
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 shrink-0 text-ink/40" />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-ink">{template.name}</div>
              <div className="truncate text-xs text-ink/55">{template.slug}</div>
            </div>
          </div>
        </td>
        <td className="px-5 py-3.5">
          <Badge variant={categoryVariant[template.category]} className="capitalize">
            {template.category}
          </Badge>
        </td>
        <td className={`${TD} text-ink/60`}>{template.department ?? '-'}</td>
        <td className={`${TD} font-mono text-ink/60`}>{template.referenceTypeCode ?? '-'}</td>
        <td className="px-5 py-3.5">
          <Badge variant={template.isActive ? 'success' : 'neutral'}>
            {template.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </td>
        <td className={`${TD} text-ink/60`}>{formatDate(template.updatedAt)}</td>
        <td className={TD}>
          {isAdmin ? (
            <div className="flex items-center gap-4">
              <Link
                to={ROUTES.ADMIN_EMAIL_TEMPLATE_EDIT(template.id)}
                className="flex items-center gap-1 font-semibold text-brand transition-colors hover:text-brand-hover"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Link>
              {template.hasBeenUsed ? (
                <button
                  type="button"
                  onClick={() => toggleActive(template)}
                  disabled={updateTemplate.isPending}
                  title="This template has been used to send emails, so it can only be deactivated, not deleted."
                  className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1 text-xs font-semibold text-ink/70 shadow-sm transition-colors hover:bg-cream hover:text-ink disabled:opacity-50"
                >
                  {template.isActive ? (
                    <PowerOff className="h-3.5 w-3.5" />
                  ) : (
                    <Power className="h-3.5 w-3.5" />
                  )}
                  {template.isActive ? 'Deactivate' : 'Activate'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleteTarget(template)}
                  className="flex items-center gap-1.5 font-semibold text-danger transition-colors hover:text-danger-hover active:text-danger-hover"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              )}
            </div>
          ) : (
            <span className="text-xs text-ink/40">View only</span>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Email Templates
          </h1>
          <p className="mt-1.5 text-sm text-ink/60">
            Configure the email templates Sales, Billing, Service, and Renewal will use when sending
            emails.
          </p>
        </div>
        {isAdmin && (
          <Link to={ROUTES.ADMIN_EMAIL_TEMPLATE_NEW}>
            <Button type="button" className="flex items-center gap-1.5">
              <Plus className="h-4 w-4" />
              New Template
            </Button>
          </Link>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Select
          aria-label="Filter by category"
          value={category}
          onChange={(e) => setCategory(e.target.value as EmailTemplateCategory | '')}
          options={[{ value: '', label: 'All categories' }, ...EMAIL_TEMPLATE_CATEGORY_OPTIONS]}
          className="w-48"
        />
        {category && (
          <button
            type="button"
            onClick={() => setCategory('')}
            className="text-sm font-semibold text-ink/50 transition-colors hover:text-ink"
          >
            Clear filter
          </button>
        )}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-line bg-white shadow-sm">
        <div
          className={
            isFetching && !isLoading ? 'opacity-60 transition-opacity' : 'transition-opacity'
          }
        >
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-line bg-cream/60">
                <th className={TH}>Template</th>
                <th className={TH}>Category</th>
                <th className={TH}>Department</th>
                <th className={TH}>Ref code</th>
                <th className={TH}>Status</th>
                <th className={TH}>Updated</th>
                <th className={TH}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">{renderBody()}</tbody>
          </table>
        </div>
      </div>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this template?</DialogTitle>
            <DialogDescription>
              This permanently removes{' '}
              <span className="font-semibold text-ink">{deleteTarget?.name}</span>. This template
              has never been used to send an email, so it can be safely deleted. This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={deleteTemplate.isPending}
              onClick={onConfirmDelete}
            >
              {deleteTemplate.isPending ? 'Deleting…' : 'Delete template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailTemplatesPage;
